// src/lib/sounds.ts
// Web Audio API synthesized sound system - no external files needed

type SoundName = 'found' | 'pop' | 'ding' | 'camera' | 'celebration' | 'hmm'

let audioContext: AudioContext | null = null
let muted = false

function getContext(): AudioContext {
  if (!audioContext) {
    audioContext = new AudioContext()
  }
  if (audioContext.state === 'suspended') {
    audioContext.resume()
  }
  return audioContext
}

function playNote(
  freq: number,
  duration: number,
  startTime: number,
  type: OscillatorType = 'sine',
  gain: number = 0.3
): void {
  const ctx = getContext()
  const osc = ctx.createOscillator()
  const gainNode = ctx.createGain()

  osc.type = type
  osc.frequency.setValueAtTime(freq, startTime)

  gainNode.gain.setValueAtTime(gain, startTime)
  gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration)

  osc.connect(gainNode)
  gainNode.connect(ctx.destination)

  osc.start(startTime)
  osc.stop(startTime + duration)
}

// Ascending chime: C5 -> E5 -> G5 -> C6, each 150ms with slight overlap
function playFound(): void {
  const ctx = getContext()
  const now = ctx.currentTime
  const notes = [523.25, 659.25, 783.99, 1046.5] // C5, E5, G5, C6
  const noteDuration = 0.3
  const noteSpacing = 0.15

  notes.forEach((freq, i) => {
    playNote(freq, noteDuration, now + i * noteSpacing, 'sine', 0.25)
  })
}

// Bubble pop: quick frequency sweep from 400Hz to 800Hz then decay
function playPop(): void {
  const ctx = getContext()
  const now = ctx.currentTime

  const osc = ctx.createOscillator()
  const gainNode = ctx.createGain()

  osc.type = 'sine'
  osc.frequency.setValueAtTime(400, now)
  osc.frequency.exponentialRampToValueAtTime(800, now + 0.1)

  gainNode.gain.setValueAtTime(0.35, now)
  gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.3)

  osc.connect(gainNode)
  gainNode.connect(ctx.destination)

  osc.start(now)
  osc.stop(now + 0.3)
}

// Bell ding: single note at 880Hz (A5) with quick attack and medium decay
function playDing(): void {
  const ctx = getContext()
  const now = ctx.currentTime

  const osc = ctx.createOscillator()
  const gainNode = ctx.createGain()

  osc.type = 'sine'
  osc.frequency.setValueAtTime(880, now)

  // Quick attack, medium decay
  gainNode.gain.setValueAtTime(0.001, now)
  gainNode.gain.linearRampToValueAtTime(0.4, now + 0.01)
  gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.5)

  osc.connect(gainNode)
  gainNode.connect(ctx.destination)

  osc.start(now)
  osc.stop(now + 0.5)
}

// Shutter click: short burst of filtered white noise
function playCamera(): void {
  const ctx = getContext()
  const now = ctx.currentTime
  const duration = 0.05

  // Create white noise buffer
  const bufferSize = ctx.sampleRate * duration
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1
  }

  const noise = ctx.createBufferSource()
  noise.buffer = buffer

  // Bandpass filter at 2000Hz
  const filter = ctx.createBiquadFilter()
  filter.type = 'bandpass'
  filter.frequency.setValueAtTime(2000, now)
  filter.Q.setValueAtTime(1.5, now)

  const gainNode = ctx.createGain()
  gainNode.gain.setValueAtTime(0.6, now)
  gainNode.gain.exponentialRampToValueAtTime(0.001, now + duration)

  noise.connect(filter)
  filter.connect(gainNode)
  gainNode.connect(ctx.destination)

  noise.start(now)
  noise.stop(now + duration)

  // Add a second quieter click slightly after for realism
  const buffer2 = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
  const data2 = buffer2.getChannelData(0)
  for (let i = 0; i < bufferSize; i++) {
    data2[i] = Math.random() * 2 - 1
  }

  const noise2 = ctx.createBufferSource()
  noise2.buffer = buffer2

  const filter2 = ctx.createBiquadFilter()
  filter2.type = 'bandpass'
  filter2.frequency.setValueAtTime(2000, now + 0.08)
  filter2.Q.setValueAtTime(1.5, now + 0.08)

  const gainNode2 = ctx.createGain()
  gainNode2.gain.setValueAtTime(0.3, now + 0.08)
  gainNode2.gain.exponentialRampToValueAtTime(0.001, now + 0.08 + duration)

  noise2.connect(filter2)
  filter2.connect(gainNode2)
  gainNode2.connect(ctx.destination)

  noise2.start(now + 0.08)
  noise2.stop(now + 0.08 + duration)
}

// Victory fanfare: arpeggio then major chord
function playCelebration(): void {
  const ctx = getContext()
  const now = ctx.currentTime

  // Arpeggio: C5, E5, G5, C6 individually
  const arpeggioNotes = [523.25, 659.25, 783.99, 1046.5]
  const noteSpacing = 0.1

  arpeggioNotes.forEach((freq, i) => {
    playNote(freq, 0.25, now + i * noteSpacing, 'sine', 0.2)
    playNote(freq, 0.25, now + i * noteSpacing, 'triangle', 0.1)
  })

  // Final chord: C5 + E5 + G5 together with long decay
  const chordStart = now + arpeggioNotes.length * noteSpacing + 0.05
  const chordDuration = 1.2

  const chordNotes = [523.25, 659.25, 783.99]
  chordNotes.forEach((freq) => {
    playNote(freq, chordDuration, chordStart, 'sine', 0.18)
    playNote(freq, chordDuration, chordStart, 'triangle', 0.08)
  })

  // Add the high C6 to the chord for brightness
  playNote(1046.5, chordDuration, chordStart, 'sine', 0.12)
}

// Gentle descending tone: 500Hz gliding down to 300Hz
function playHmm(): void {
  const ctx = getContext()
  const now = ctx.currentTime

  const osc = ctx.createOscillator()
  const gainNode = ctx.createGain()

  osc.type = 'sine'
  osc.frequency.setValueAtTime(500, now)
  osc.frequency.exponentialRampToValueAtTime(300, now + 0.4)

  gainNode.gain.setValueAtTime(0.25, now)
  gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.5)

  osc.connect(gainNode)
  gainNode.connect(ctx.destination)

  osc.start(now)
  osc.stop(now + 0.5)
}

const soundPlayers: Record<SoundName, () => void> = {
  found: playFound,
  pop: playPop,
  ding: playDing,
  camera: playCamera,
  celebration: playCelebration,
  hmm: playHmm,
}

export const SoundManager = {
  play(name: SoundName): void {
    if (muted) return
    try {
      soundPlayers[name]()
    } catch {
      // Silently fail if Web Audio API is not available
    }
  },

  mute(): void {
    muted = true
  },

  unmute(): void {
    muted = false
  },

  isMuted(): boolean {
    return muted
  },

  toggle(): boolean {
    if (muted) {
      this.unmute()
    } else {
      this.mute()
    }
    return muted
  },

  preloadAll(): void {
    // No-op: sounds are generated programmatically via Web Audio API
    // Calling getContext() here could fail without user interaction,
    // so we lazily initialize on first play instead.
  },
}
